/**
 * The render half of AMD-24 ("no edge"), plus the curved-horizon projection.
 *
 * Two transforms, one place, applied identically on the CPU and on the GPU. Every world
 * position the renderer draws — instance origins, ribbon vertices, the floor lattice —
 * goes through `warpWorld`, and every screen-space projection the renderer does by hand
 * (the post-composite player rim, the radar's off-screen arrows) goes through the JS
 * mirror of the same function. If the two ever disagree the arrow points at a body that
 * is not there, so they are written next to each other on purpose.
 *
 * **1. Seam wrap — the 3×3 neighbourhood, collapsed.**
 *
 * The arena is a flat torus of period 200 (`src/sim/wrap.ts`). Drawing an entity near
 * the seam needs its image on the player's side of the cut, and the textbook answer is
 * to replicate every instance across the 3×3 neighbourhood and cull the eight replicas
 * that miss the view. Every one of those replicas is a translation by a whole period,
 * and the widest framing the game ever shows is 68 u × 1.28 cinematic zoom = 87 u, so at
 * most ONE image of any instance can intersect the view:
 *
 *     viewHalf (43.5 u) + longest instance reach (a 45 u boss aim line) = 88.5 u < 100 u
 *
 * The replication therefore collapses to picking the single surviving replica, which is
 * exactly `iPos - period · round((iPos - camera) / period)`. That is the 3×3 pass with
 * the cull already applied: per-instance, branch-free, no extra instances, no extra draw
 * calls, and no per-frame CPU. The whole seam costs three uniforms and four lines of
 * vertex shader.
 *
 * **2. Curved horizon — the "surface of a planet".**
 *
 * Camera-relative offsets are divided by `1 + c·r²`, with `r` the distance from the
 * focus point in units of the view half-diagonal. Points near the middle of the frame do
 * not move; points at the rim are pulled inward, so straight sonar-grid lines bow and
 * the field reads as the top of a sphere rather than a plane. It runs in the same
 * direction as the CRT barrel in `crt.ts` (which bulges the *sampled image* outward at
 * 0.018/0.026) rather than against it: the world curves, the tube then bulges the
 * picture of it, and the two compose into one convex surface.
 *
 * The readability guard (AMD-3, AMD-6, AMD-7) is structural rather than tuned: only the
 * instance ORIGIN is curved, never the instance's own local geometry. A telegraph ring
 * at the rim is displaced but is still a circle of the authored radius, a hazard band is
 * still `uBand.x` wide, and the §11.9 edge contrast is untouched by construction. `c` is
 * `render.curvature` (0 turns it off outright) and is scaled down on small viewports —
 * see `curvatureForViewport`.
 */

import * as THREE from 'three';
import { WRAP_H, WRAP_W, wrapDeltaX, wrapDeltaY } from '../sim/wrap';

/**
 * The GLSL half. Declares the three uniforms and the shared transform; any vertex
 * program that draws in world space includes this and calls `warpWorld` exactly once.
 */
export const GLSL_WARP = /* glsl */ `
uniform vec2 uWrapCenter;
/** Torus period, or 0 on an axis that must not wrap (screen-space overlays, trails). */
uniform vec2 uWrapPeriod;
/**
 * x = curvature amount, y = 1 / (view half-diagonal)². The reciprocal is squared so the
 * transform needs no square root: r² is a dot product, and both the shader and the JS
 * mirror run this per entity per frame.
 */
uniform vec2 uCurve;

vec2 warpWorld(vec2 w) {
  if (uWrapPeriod.x > 0.0) {
    w -= uWrapPeriod * floor((w - uWrapCenter) / uWrapPeriod + 0.5);
  }
  if (uCurve.x > 0.0) {
    vec2 v = w - uWrapCenter;
    w = uWrapCenter + v / (1.0 + uCurve.x * dot(v, v) * uCurve.y);
  }
  return w;
}
`;

/**
 * Shared uniform objects. Three.js reads `IUniform.value` at draw time and never copies
 * the object, so handing the SAME object to every world material means one write per
 * frame updates all ~50 of them. That is the only reason this is module state.
 */
const U_CENTER: THREE.IUniform<THREE.Vector2> = { value: new THREE.Vector2() };
const U_CURVE: THREE.IUniform<THREE.Vector2> = { value: new THREE.Vector2(0, 0) };
const U_PERIOD_TORUS: THREE.IUniform<THREE.Vector2> = { value: new THREE.Vector2(WRAP_W, WRAP_H) };
const U_PERIOD_OFF: THREE.IUniform<THREE.Vector2> = { value: new THREE.Vector2(0, 0) };
const U_CURVE_OFF: THREE.IUniform<THREE.Vector2> = { value: new THREE.Vector2(0, 0) };

/** Instances live at raw world positions and must be pulled to the nearest image. */
export function worldWarpUniforms(): Record<string, THREE.IUniform> {
  return { uWrapCenter: U_CENTER, uWrapPeriod: U_PERIOD_TORUS, uCurve: U_CURVE };
}

/**
 * Geometry the CPU has already resolved to one image — ribbon vertices, the floor
 * lattice. Wrapping these per-vertex would tear a ribbon that straddles the cut, so the
 * period is off and only the curve applies.
 */
export function unwrappedWarpUniforms(): Record<string, THREE.IUniform> {
  return { uWrapCenter: U_CENTER, uWrapPeriod: U_PERIOD_OFF, uCurve: U_CURVE };
}

/** Screen-space overlays (the AMD-8 radar): neither transform may touch them. */
export function screenWarpUniforms(): Record<string, THREE.IUniform> {
  return { uWrapCenter: U_CENTER, uWrapPeriod: U_PERIOD_OFF, uCurve: U_CURVE_OFF };
}

// --- the JS mirror ---------------------------------------------------------------------

let centerX = 0;
let centerY = 0;
let amount = 0;
/** `amount / halfDiagonal²`, folded so `warpOffset` is four multiplies and a divide. */
let curveK = 0;

/**
 * Camera-relative warped offset of the last `warpOffset` / `warpPoint` call. Module
 * scope so the hot path allocates nothing (INTERFACES ground rule 2).
 */
export const WARP_OUT = { x: 0, y: 0 };

/**
 * Publishes this frame's transform to every world material and to the JS mirror. Called
 * once per frame from `Renderer.render`, immediately after the camera has settled and
 * before anything is culled or pushed.
 */
export function setWarp(camX: number, camY: number, curveAmount: number, halfDiagonal: number): void {
  centerX = camX;
  centerY = camY;
  amount = curveAmount > 0 ? curveAmount : 0;
  const invR2 = halfDiagonal > 1e-4 ? 1 / (halfDiagonal * halfDiagonal) : 1;
  curveK = amount * invR2;
  U_CENTER.value.set(camX, camY);
  U_CURVE.value.set(amount, invR2);
}

/**
 * Writes the camera-relative warped offset of a world point into `WARP_OUT`.
 *
 * This runs once per drawable entity per frame through `camera.contains`, so it is
 * written for the hot path: no square root, no allocation, and the curvature term is one
 * fused constant. An earlier draft used `Math.hypot`, which V8 implements with overflow
 * guards, and it cost about a third of a millisecond a frame at the stress seed.
 */
export function warpOffset(x: number, y: number): void {
  let dx = wrapDeltaX(x - centerX);
  let dy = wrapDeltaY(y - centerY);
  if (amount > 0) {
    const f = 1 / (1 + curveK * (dx * dx + dy * dy));
    dx *= f;
    dy *= f;
  }
  WARP_OUT.x = dx;
  WARP_OUT.y = dy;
}

/**
 * Smallest local linear scale the curvature applies at a world point, in 0..1.
 *
 * AMD-27 needs this. The curve maps a camera-relative radius `r` to `r / (1 + k·r²)`,
 * which is a *contraction*: two world points a fixed distance apart land closer together
 * on screen the further out they sit. Damage-number glyphs are not curved (only the
 * instance origin is — see the header), so a pair of numbers that clears by 3 px in the
 * middle of the frame can be touching at the rim. The placement inflates its keep-out
 * boxes by the reciprocal of this number.
 *
 * The radial derivative `d/dr [r/(1+kr²)] = (1-kr²)/(1+kr²)²` is always the tighter of
 * the two axes (the tangential one is just `1/(1+kr²)`), so it is the only one computed.
 * `r` is padded by 15% before the evaluation: a popup lives 0.6 s, and the camera can
 * carry it outward in that time.
 */
export function warpMinScale(x: number, y: number): number {
  if (amount <= 0) return 1;
  const dx = wrapDeltaX(x - centerX);
  const dy = wrapDeltaY(y - centerY);
  const q = curveK * (dx * dx + dy * dy) * 1.32;
  const d = 1 + q;
  const radial = (1 - q) / (d * d);
  return radial < 0.4 ? 0.4 : radial > 1 ? 1 : radial;
}

/** Writes the warped absolute world position into `WARP_OUT`. Matches `warpWorld`. */
export function warpPoint(x: number, y: number): void {
  warpOffset(x, y);
  WARP_OUT.x += centerX;
  WARP_OUT.y += centerY;
}

/**
 * Curvature strength for a viewport, following the AMD-6 D12 precedent that turns the
 * barrel off below the desktop framing.
 *
 * D12's argument is that a bowed frame on a six-inch screen costs the corners and buys
 * nothing, and it applies twice over here: a phone shows 20 u of width, so the rim IS
 * the playfield and a strong curve would push live gameplay into the distorted band.
 * Full strength at 900 CSS px of height and above; below that it ramps to at most 35% of
 * the configured amount, reaching zero at 480 px. `cssHeight`, not physical pixels — a
 * phone at DPR 2 is still a phone.
 */
export function curvatureForViewport(cssHeight: number): number {
  if (cssHeight >= 900) return 1;
  const t = (cssHeight - 480) / 420;
  return 0.35 * (t <= 0 ? 0 : t >= 1 ? 1 : t);
}
